import { Injectable } from '@angular/core';
import { TicketsAndFaresStoreFacade } from '@modules/ticket-fares-store';
import { ITicketProductWithBundleType } from '@nx-ng/types';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { Basket, BasketItem } from '../client/src';
import { formatBasketItemForAPI } from '../effects/util';
import { CheckoutStoreFacade } from '../facade';
import { BasketDeleted, CreateBasketItem } from '../types';
import {
  getQuantityOfItemCurrentlyInBasket,
  shouldDeleteBasket,
} from '../util/basket-update';
import { CheckoutService } from './checkout.service';
import { sumBy } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class DetermineBasketUpdateService {
  constructor(
    private checkoutFacade: CheckoutStoreFacade,
    private ticketAndFaresFacade: TicketsAndFaresStoreFacade,
    private checkoutService: CheckoutService
  ) {}

  public addItemToBasketFromQueue(
    queue: CreateBasketItem[] | undefined
  ): Observable<[CreateBasketItem[], Basket?]> {
    return combineLatest([
      of(queue),
      this.ticketAndFaresFacade.allFares$,
      this.checkoutFacade.selectBasket$,
    ]).pipe(
      take(1),
      switchMap(
        ([queue, allTickets, basket]: [
          CreateBasketItem[] | undefined,
          ITicketProductWithBundleType[],
          Basket?
        ]) => {
          return this.performBasketAddFromQueue({
            queue,
            allTickets,
            basket,
          });
        }
      )
    );
  }

  public performBasketUpdate(
    item: CreateBasketItem
  ): Observable<Basket | BasketDeleted | undefined> {
    return combineLatest([
      of(item),
      this.ticketAndFaresFacade.allFares$,
      this.checkoutFacade.selectBasket$,
    ]).pipe(
      take(1),
      switchMap(
        ([item, allTickets, basket]: [
          CreateBasketItem,
          ITicketProductWithBundleType[],
          Basket?
        ]) => {
          return this.determineBasketUpdateActionAndExecute({
            item,
            allTickets,
            basket,
          });
        }
      )
    );
  }

  private determineBasketUpdateActionAndExecute({
    item,
    allTickets,
    basket,
  }: {
    item: CreateBasketItem;
    basket?: Basket;
    allTickets: ITicketProductWithBundleType[];
  }): Observable<Basket | BasketDeleted | undefined> {
    const basketItem: BasketItem | undefined = formatBasketItemForAPI(
      item,
      allTickets
    );

    if (!basketItem) {
      return EMPTY;
    }

    if (!basket) {
      return this.checkoutService.createBasket(basketItem);
    }

    const currentItemQuantityInBasket = getQuantityOfItemCurrentlyInBasket(
      basket,
      item.ticketID
    );

    if (
      shouldDeleteBasket({
        requestQuantity: basketItem.quantity,
        currentItemQuantityInBasket,
        totalItemsQuantityCurrentlyInBasket: sumBy(basket.basketItems, (item) => item.quantity),
      })
    ) {
      return this.checkoutService.deleteBasket(basket.basketId);
    }

    if (basketItem.quantity === 0) {
      return this.checkoutService.removeItemFromBasket(
        basket.basketId,
        basketItem.ticketID
      );
    }

    if (currentItemQuantityInBasket === 0) {
      return this.checkoutService.addItemToBasket(basket.basketId, basketItem);
    }

    return this.checkoutService.updateItemInBasket(basket.basketId, basketItem);
  }

  private performBasketAddFromQueue({
    queue,
    basket,
    allTickets,
  }: {
    queue: CreateBasketItem[] | undefined;
    basket?: Basket;
    allTickets: ITicketProductWithBundleType[];
  }): Observable<[CreateBasketItem[], Basket?]> {
    if (!queue || queue.length == 0) {
      return EMPTY;
    }

    const basketItem: BasketItem | undefined = formatBasketItemForAPI(
      queue[0],
      allTickets
    );

    if (!basketItem) {
      return EMPTY;
    }

    if (!basket) {
      return combineLatest([
        of(queue),
        this.checkoutService.createBasket(basketItem),
      ]).pipe(take(1));
    }

    return combineLatest([
      of(queue),
      this.checkoutService.addItemToBasket(basket.basketId, basketItem),
    ]).pipe(take(1));
  }
}
